import Box from '@mui/material/Box'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useMemo } from 'react'

import { useActivity } from 'components/configured-service/accordions/ActivityAccordion/ActivityProvider'
import { getActivityCellDisplay } from 'components/configured-service/accordions/ActivityAccordion/renderActivityCellUtils'
import LocationMapImage from 'components/customer-details/content/location/LocationMapImage'
import Tag from 'components/tags/Tag'
import { formatDefault, formatDateOnly } from 'utils/activity/activityFormatters'

import type { GridRenderCellParams } from '@mui/x-data-grid-pro'

const IMAGE_HEIGHT = 120
const IMAGE_WIDTH = 120

// null, not undefined — undefined would trigger Tag's default maxWidth and truncate these.
const UNBOUNDED_TAG_WIDTH = null as unknown as number

type ActivityTag = { id?: string; tagName?: string | null; tagColor?: string | null }

type ActivityCellRow = {
  id?: string
  modifiedByName?: string
  modifiedByUsername?: string
  modifiedAt?: string
  changeType?: string
  field?: string
  fieldKey?: string
  oldValue?: unknown
  newValue?: unknown
  containerName?: string
  renderRawValue?: boolean
  eventDate?: string | null
  workOrderNumber?: string | null
}

type ValueFormatter = (value: unknown, lookups?: unknown) => React.ReactNode

const ActivityCell = ({ row: rawRow }: GridRenderCellParams) => {
  const row = rawRow as ActivityCellRow
  const { modifiedByName, modifiedByUsername, modifiedAt, changeType } = row

  const isGeoCoords = row.fieldKey === 'geoLocation'
  const coords = isGeoCoords && typeof row.newValue === 'string' ? (row.newValue || ' ').split(', ') : []
  const [prevLat, prevLng] = coords

  const display = useMemo(() => getActivityCellDisplay(row), [row])

  const activityCtx = useActivity()
  const lookups = activityCtx?.lookups
  const registryFromContext = activityCtx?.formatterRegistry || {}
  const fallbackRegistry = useMemo(
    () => ({
      serviceDate: formatDateOnly,
    }),
    []
  )
  const formatterRegistry: Record<string, unknown> = { ...fallbackRegistry, ...registryFromContext }
  const formatter = (formatterRegistry[row.fieldKey ?? ''] as ValueFormatter) || (formatDefault as ValueFormatter)
  const displayOldValue = formatter(row.oldValue, lookups)
  const displayNewValue = formatter(row.newValue, lookups)

  return (
    <ListItem secondaryAction={modifiedAt}>
      <ListItemText
        disableTypography
        primary={
          <Stack direction="column" spacing={1}>
            <Stack useFlexGap alignItems="center" direction="row" flexWrap="wrap" spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
              <Typography color="text.primary" fontWeight={600} variant="body1">
                {modifiedByName}
              </Typography>
              {modifiedByUsername && (
                <Typography color="text.secondary" variant="body1">
                  {`| ${modifiedByUsername}`}
                </Typography>
              )}
              {display.isRemovedContainer ? (
                <>
                  <Typography color="text.primary" sx={{ fontWeight: 700 }} variant="body1">
                    {display.titleText}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: 'error.light', textTransform: 'lowercase' }} variant="body1">
                    removed
                  </Typography>
                </>
              ) : (
                <>
                  <Typography color="text.primary" sx={{ fontWeight: 700 }} variant="body1">
                    {display.titleText}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: display.changeColor }} textTransform="lowercase" variant="body1">
                    {changeType}
                  </Typography>
                  {display.valueVariant === 'single' && (
                    <>
                      {row.renderRawValue ? (
                        <>{display.singleSource === 'new' ? displayNewValue : displayOldValue}</>
                      ) : (
                        <Typography color="text.primary" variant="body1">
                          &apos;
                          {display.singleSource === 'new' ? displayNewValue : displayOldValue}
                          &apos;
                        </Typography>
                      )}
                    </>
                  )}
                  {row.fieldKey === 'tagRef' ? (
                    <Box alignItems="center" display="flex" flexWrap="wrap" gap={1} sx={{ minWidth: 0 }}>
                      {Array.isArray(row.newValue) && row.newValue.length > 0 && (
                        <>
                          {(row.newValue as ActivityTag[]).map((tag, idx) => (
                            <React.Fragment key={tag.id || idx}>
                              <Tag clickable={false} maxWidth={UNBOUNDED_TAG_WIDTH} size="small" tag={tag} />
                            </React.Fragment>
                          ))}
                        </>
                      )}
                      {Array.isArray(row.oldValue) && row.oldValue.length > 0 && (
                        <>
                          {(row.oldValue as ActivityTag[]).map((tag, idx) => (
                            <React.Fragment key={tag.id || idx}>
                              <Tag clickable={false} maxWidth={UNBOUNDED_TAG_WIDTH} size="small" tag={tag} />
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </Box>
                  ) : (
                    <>
                      {display.valueVariant === 'pair' && (
                        <>
                          {row.oldValue !== '' && (
                            <>
                              <Typography color="text.primary" variant="body1">
                                from
                              </Typography>
                              {row.fieldKey === 'servicePrice' || row.renderRawValue ? (
                                <>{displayOldValue}</>
                              ) : (
                                <Typography color="text.primary" variant="body1">
                                  &apos;
                                  {displayOldValue}
                                  &apos;
                                </Typography>
                              )}
                            </>
                          )}
                          <Typography color="text.primary" variant="body1">
                            to
                          </Typography>
                          {row.fieldKey === 'servicePrice' || row.renderRawValue ? (
                            <>{displayNewValue}</>
                          ) : (
                            <Typography color="text.primary" variant="body1">
                              &apos;
                              {displayNewValue}
                              &apos;
                            </Typography>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {display.showOnContainer && (
                    <>
                      <Typography color="text.primary" variant="body1">
                        on
                      </Typography>
                      <Typography color="text.primary" sx={{ fontWeight: 700 }} variant="body1">
                        {row.containerName}
                      </Typography>
                    </>
                  )}
                  {row.eventDate && (
                    <>
                      <Typography color="text.primary" variant="body1">
                        on
                      </Typography>
                      <Typography color="text.primary" sx={{ fontWeight: 700 }} variant="body1">
                        {formatDateOnly(row.eventDate)}
                      </Typography>
                    </>
                  )}
                  {row.workOrderNumber && (
                    <Typography color="text.secondary" variant="body1">
                      {`(${row.workOrderNumber})`}
                    </Typography>
                  )}
                </>
              )}
            </Stack>

            {isGeoCoords && prevLat && prevLng && (
              <Stack alignItems="center" direction="row" sx={{ mt: 1 }}>
                <Box sx={{ mr: 2 }}>
                  <LocationMapImage
                    containerId={row.id}
                    height={IMAGE_HEIGHT}
                    latitude={prevLat}
                    longitude={prevLng}
                    showInvalidAlert={false}
                    width={IMAGE_WIDTH}
                    zoom={14}
                  />
                </Box>
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    height: '100%',
                  }}
                >
                  <ListItemText
                    primary={row.containerName}
                    secondary={`${prevLat || 0}, ${prevLng || 0}`}
                    slotProps={{
                      primary: {
                        variant: 'h6',
                        fontWeight: 500,
                        color: 'textPrimary',
                      },
                      secondary: {
                        variant: 'body1',
                        color: 'textSecondary',
                      },
                    }}
                  />
                </Stack>
              </Stack>
            )}
          </Stack>
        }
        sx={{ minWidth: 0, width: '100%', m: 0, '& .MuiListItemText-primary': { width: '100%' } }}
      />
    </ListItem>
  )
}

export default ActivityCell
